// Jamora Assist: alerts in the technician's browser when a request arrives in the queue (sound, system
// notification, tab title, toast). A convenience for whoever has the CRM open; WhatsApp (sent by the server when
// Jamora reports the request) is what reaches a technician whose CRM is closed. Pure helpers first (tested), then the
// few browser calls, each one tolerant of an API the browser lacks or refuses.

const ALERTED_KEY_PREFIX = "jamora:assist-alerted:";
const ALERTED_TTL_MS = 60 * 60 * 1000;
const BASE_TITLE = "Jamora CRM";

// Requests present now that were not in the previous reading. The very first reading is a baseline: what was
// already waiting when the CRM opened is shown by the badge, not announced as new.
export function newArrivals(previousIds, items) {
  if (previousIds === null) return [];
  return items.filter((item) => !previousIds.has(item.id));
}

export function titleWithCount(count, base = BASE_TITLE) {
  return count > 0 ? `(${count}) ${base}` : base;
}

export function arrivalMessage(items) {
  if (items.length === 1) return `Nouvelle demande d'assistance : ${items[0].pharmacy || "Pharmacie"}`;
  return `${items.length} nouvelles demandes d'assistance`;
}

// Several CRM tabs open: only the first one to see a request rings and notifies.
export function claimAlert(id, storage = safeStorage(), now = Date.now()) {
  if (!storage) return true;
  try {
    const key = ALERTED_KEY_PREFIX + id;
    const previous = Number(storage.getItem(key));
    if (previous && now - previous < ALERTED_TTL_MS) return false;
    storage.setItem(key, String(now));
    return true;
  } catch {
    return true;
  }
}

function safeStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function notificationsSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function notificationPermission() {
  return notificationsSupported() ? window.Notification.permission : "unsupported";
}

export async function requestNotificationPermission() {
  if (!notificationsSupported()) return "unsupported";
  try {
    return await window.Notification.requestPermission();
  } catch {
    return window.Notification.permission;
  }
}

// System notification, one per request (`tag`), only once the technician allowed it. Clicking it brings the CRM back.
export function showSystemNotification(item, onClick) {
  if (notificationPermission() !== "granted") return;
  try {
    const notification = new window.Notification("Nouvelle demande d'assistance", {
      body: item.pharmacy || "Pharmacie",
      tag: `assist-${item.id}`,
    });
    notification.onclick = () => {
      window.focus();
      onClick();
      notification.close();
    };
  } catch {
    // Some browsers only allow notifications from a service worker: the toast and the title still say it.
  }
}

// Two short tones, generated (no audio file to ship). Browsers allow sound only after the person has interacted with
// the page at least once; before that, this stays silent rather than failing.
let audioContext = null;
export function playArrivalSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    audioContext = audioContext || new AudioCtx();
    if (audioContext.state === "suspended") audioContext.resume().catch(() => {});
    const start = audioContext.currentTime;
    [880, 1175].forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      const at = start + index * 0.18;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.2, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
      oscillator.connect(gain).connect(audioContext.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.17);
    });
  } catch {
    // No sound available: the other alerts remain.
  }
}
